import React, { ReactNode } from 'react'

import Icon, { IconProps } from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Tooltip from 'components_new/atoms/Tooltip'

export interface IconTipProps {
  disabled?: boolean
  edge?: 'end' | 'start'
  icon?: IconProps
  placement?:
    | 'bottom-end'
    | 'bottom-start'
    | 'bottom'
    | 'left-end'
    | 'left-start'
    | 'left'
    | 'right-end'
    | 'right-start'
    | 'right'
    | 'top-end'
    | 'top-start'
    | 'top'
  size?: 'xsmall' | 'small' | 'medium'
  sx?: object
  title: ReactNode
  variant?: 'info' | 'help' | 'note'
}

/**
 * The IconTip component is used to show
 * an informative tooltip when hovering
 * an icon.
 *
 * 'info' is used for providing users with
 * an extra bit of insight or knowledge.
 * Information that is not critical, but
 * still really good to know.
 *
 * 'help' is used if the user has to take
 * an action that might trigger a “…but
 * why?”, “…what does this mean?” etc.
 *
 * 'note' is used when there’s information
 * about a field or action that your user
 * should know before they move forward
 * (but you feel that most might be aware
 * of this).
 *
 * It is also possible to set a custom icon
 * using the icon prop.
 */

const IconTip = React.forwardRef((props: IconTipProps, ref: any) => {
  const {
    disabled,
    edge,
    icon,
    placement,
    size,
    sx,
    title,
    variant = 'info'
  } = props

  const getFontSize = () => {
    switch (size) {
    case 'xsmall':
      return '0.8rem'
    case 'small':
      return '1rem'
    default:
      return '1.25rem'
    }
  }

  return (
    <Tooltip enterDelay={400} placement={placement} title={title}>
      <IconButton
        disabled={disabled}
        edge={edge}
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
        }}
        ref={ref}
        size="small"
        sx={{
          cursor: 'default',
          height: getFontSize(),
          p: 0,
          pointerEvents: disabled ? 'none' : 'all!important',
          width: getFontSize(),
          ...sx
        }}
      >
        <Icon
          name={
            variant === 'help'
              ? 'HelpOutlined'
              : variant === 'note'
                ? 'FeedbackOutlined'
                : 'Info'
          }
          {...icon}
          sx={{ fontSize: getFontSize() }}
        />
      </IconButton>
    </Tooltip>
  )
})

IconTip.displayName = 'IconTip'
export default IconTip

export const AiIconTip = ({ sx, title }: { sx?: object; title: ReactNode }) => (
  <IconTip
    icon={{ fontSize: 'small', name: 'AI', color: 'secondary' }}
    sx={sx}
    title={title}
  />
)
